import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import css from "./HlsPlayer.module.scss";
import { useIsMobile } from "~/submodules/useIsMobile";
import { useBrowser } from "~/utils";
import { useClientSize } from "~/hooks/useClientSize";

type PlayerProps = React.PropsWithChildren<{
  src: string;
  poster: string;
}>;

// Youtubeのアスペクト比に合わせています
const aspectRatio = 528 / 939;

export const HlsPlayer: React.FC<PlayerProps> = ({ src, poster }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isMobile = useIsMobile();
  const browser = useBrowser();
  const videoClientSize = useClientSize(videoRef.current);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    if (Hls.isSupported()) {
      const hls = new Hls({
        autoStartLoad: true,
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 90,
        xhrSetup(a) {
          a.withCredentials = true;
        },
      });
      hls.loadSource(src);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.ERROR, function (_eventName, data) {
        if (data.fatal) {
          console.error(`Fatal error : ${data.details}`);
          switch (data.type) {
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error(`A media error occurred: ${data.details}`);
              hls.recoverMediaError();
              return;
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error(`A network error occurred: ${data.details}`);
              return;
            default:
              console.error(`An unrecoverable error occurred: ${data.details}`);
              hls.destroy();
              return;
          }
        }
        console.warn("Error event:", data);
      });

      return () => {
        hls.removeAllListeners();
        hls.stopLoad();
      };
    } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
      videoRef.current.src = src;
    }
  }, [src]);

  const height = videoClientSize.screenWidth ? videoClientSize.screenWidth * aspectRatio : 370;

  return (
    <div className={css.player}>
      <video
        ref={videoRef}
        controls
        poster={poster}
        autoPlay
        style={{ width: "100%", height }}
        {...(isMobile && browser?.getBrowserName() === "Safari"
          ? {
              muted: true,
              playsInline: true,
            }
          : {})}
      />
    </div>
  );
};

export const HlsPlayerSkelton: React.FC<
  React.PropsWithChildren<{
    className?: string;
    imageUrl?: string;
  }>
> = ({ children, className, imageUrl }) => {
  const isMobile = useIsMobile(false);
  const divRef = useRef<HTMLDivElement>(null);
  const divClientSize = useClientSize(divRef.current);
  const height = divClientSize.screenWidth ? divClientSize.screenWidth * aspectRatio : 370;

  const [ready, setReady] = useState(false);
  const style =
    !ready || isMobile
      ? { backgroundImage: `url(${imageUrl})` }
      : { width: "100%", height, backgroundImage: `url(${imageUrl})` };

  useEffect(() => {
    setReady(true);
  }, []);

  return (
    <div ref={divRef} className={className} style={style}>
      {children}
    </div>
  );
};
